/**
 * Device name generation utilities.
 * Used by both SKU onboarding and legacy device creation flows.
 */

// Maps makeModelId to device name suffix
export const DEVICE_NAME_SUFFIX: Record<string, string> = {
  // SKU format (underscore)
  CC_AGX: "agx",
  CC_CH4: "ch4",
  CC_OPT: "fb4",
  CC_THERMAL: "fb4therm",
  CC_LWOP: "lwop",
  CC_VOC: "voc",
  CC_POE: "poe",
  // Legacy format (space)
  "CC CH4": "ch4",
  "CC FB4": "fb4",
  "CC FB4 Thermal": "fb4therm",
  V1: "multi",
};

/**
 * Generates a device name following the convention: {location}-{suffix}-{count}
 *
 * @param {string} locationTitle - The location name (e.g., "University 310")
 * @param {string} makeModelId - The device type (e.g., "CC_CH4", "CC FB4")
 * @param {string[]} existingNames - Array of existing device names at this location (lowercase)
 * @return {string} Generated device name (e.g., "university310-ch4-1")
 */
export const generateDeviceName = (
  locationTitle: string,
  makeModelId: string,
  existingNames: string[]
): string => {
  const normalized = locationTitle.replace(/\s+/g, "").toLowerCase();
  const suffix =
    DEVICE_NAME_SUFFIX[makeModelId] ??
    makeModelId.replace(/\s+/g, "").toLowerCase();

  // Find next available count
  let count = 1;
  const lowercaseNames = existingNames.map(n => n.toLowerCase());

  while (lowercaseNames.includes(`${normalized}-${suffix}-${count}`)) {
    count++;
  }

  return `${normalized}-${suffix}-${count}`;
};

/**
 * Gets the suffix for a given makeModelId.
 *
 * @param {string} makeModelId - The device type (e.g., "CC_CH4", "CC FB4")
 * @return {string} The suffix (e.g., "ch4", "fb4therm")
 */
export const getDeviceSuffix = (makeModelId: string): string => {
  return (
    DEVICE_NAME_SUFFIX[makeModelId] ??
    makeModelId.replace(/\s+/g, "").toLowerCase()
  );
};
